import type React from "react";
import { useCallback, useEffect, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import apiService from "../../../../service/Api";
import type { IService } from "../../../../types/package";
import PackageBuilderLayout from "./PackageBuilderLayout";
import GeneralTab from "./tabs/GeneralTab";
import ServicesTab from "./tabs/ServicesTab";
import OperationalRequirementsTab from "./tabs/OperationalRequirementsTab";
import PricingTab from "./tabs/PricingTab";
import {
  emptyState,
  type PackageBuilderState,
  type RoleOption,
} from "./types";

const TABS = ["General", "Services", "Operational Requirements", "Pricing"];

interface PackageDetail {
  id: number | string;
  name: string;
  package_type: string;
  description: string | null;
  base_price: number | string;
  show_on_public?: boolean;
  package_status?: string;
  company?: { id: number; name: string } | null;
  base_services?: Array<{ id: number; name: string }>;
  crew_requirements?: Array<{
    id: number;
    role_id: number;
    role_name: string;
    quantity: number;
    is_mandatory: boolean;
  }>;
}

const PackageBuilderPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [state, setState] = useState<PackageBuilderState>(emptyState());
  const [activeTab, setActiveTab] = useState(0);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);

  const [roleOptions, setRoleOptions] = useState<RoleOption[]>([]);
  const [rolesLoading, setRolesLoading] = useState(true);

  const update = useCallback(
    (patch: Partial<PackageBuilderState>) =>
      setState((prev) => ({ ...prev, ...patch })),
    [],
  );

  // --- load roles ---------------------------------------------------------
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setRolesLoading(true);
      try {
        let page = 1;
        let all: RoleOption[] = [];
        let hasMore = true;
        while (hasMore) {
          const res = await apiService.get<{
            next: string | null;
            results: RoleOption[];
          }>("/api/crews/roles/", { page, page_size: 100 });
          all = [...all, ...(res.data.results ?? [])];
          hasMore = res.data.next != null;
          page += 1;
        }
        if (!cancelled) setRoleOptions(all);
      } catch {
        if (!cancelled) toast.error("Failed to load roles.");
      } finally {
        if (!cancelled) setRolesLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // --- load package (edit) ---------------------------------------------------
  useEffect(() => {
    if (!isEdit) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await apiService.get<PackageDetail>(
          `/api/packages/package/${id}/`,
        );
        if (cancelled) return;
        const pkg = res.data;
        setState({
          name: pkg.name || "",
          package_type: pkg.package_type || "Funeral",
          description: pkg.description || "",
          base_price:
            pkg.base_price != null ? String(pkg.base_price) : "",
          show_on_public: pkg.show_on_public ?? false,
          is_active: (pkg.package_status ?? "active") === "active",
          funeral_home_id: pkg.company ? String(pkg.company.id) : "",
          base_services: (pkg.base_services || []).map((s) => ({
            id: String(s.id),
            name: s.name,
            price: 0,
            service_category: "Default",
          })) as IService[],
          crew_requirements: (pkg.crew_requirements || []).map((r) => ({
            key: `req-${r.id}`,
            role_id: r.role_id,
            role_name: r.role_name,
            quantity: r.quantity,
            is_mandatory: r.is_mandatory,
          })),
          logo_file: null,
          logo_preview: null,
        });
      } catch {
        if (!cancelled) toast.error("Failed to load package.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, isEdit]);

  const validate = (): string | null => {
    if (!state.name.trim()) return "Package name is required.";
    if (state.base_price === "" || Number(state.base_price) < 0)
      return "A valid base price is required.";
    if (state.package_type === "FuneralHome" && !state.funeral_home_id)
      return "Select the funeral home this package belongs to.";
    const filled = state.crew_requirements.filter((r) => r.role_id !== "");
    const roleIds = filled.map((r) => r.role_id);
    if (new Set(roleIds).size !== roleIds.length)
      return "Each crew requirement must use a different role.";
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const payload: Record<string, unknown> = {
        name: state.name.trim(),
        package_type: state.package_type,
        description: state.description.trim(),
        base_price: Number(state.base_price || 0),
        package_status: state.is_active ? "active" : "in_active",
        show_on_public: state.show_on_public,
        base_services_ids: state.base_services.map((s) => Number(s.id)),
        crew_requirements_input: state.crew_requirements
          .filter((r) => r.role_id !== "")
          .map((r) => ({
            role_id: Number(r.role_id),
            quantity: Number(r.quantity) || 1,
            is_mandatory: r.is_mandatory,
          })),
      };
      if (state.package_type === "FuneralHome" && state.funeral_home_id) {
        payload.company_id = Number(state.funeral_home_id);
      }

      let packageId: number | string | undefined = id;
      if (isEdit) {
        await apiService.put(`/api/packages/package/${id}/`, payload);
      } else {
        const res = await apiService.post<{ id: number }>(
          "/api/packages/package/",
          payload,
        );
        packageId = res.data.id;
      }

      if (state.logo_file && packageId) {
        const fd = new FormData();
        fd.append("package_logo", state.logo_file);
        await apiService.patch(`/api/packages/package/${packageId}/`, fd);
      }

      toast.success(isEdit ? "Package updated." : "Package created.");
      navigate("/configurations/package");
    } catch (e: unknown) {
      const data = (e as { response?: { data?: Record<string, unknown> } })
        ?.response?.data;
      const msg =
        (data && typeof data === "object"
          ? Object.entries(data)
              .map(
                ([k, v]) =>
                  `${k}: ${Array.isArray(v) ? v.join(", ") : String(v)}`,
              )
              .join("; ")
          : "") || "Failed to save package.";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <PackageBuilderLayout
      breadcrumb={isEdit ? state.name || "Edit Package" : "New Package"}
      tabs={TABS}
      activeTab={activeTab}
      onTabChange={setActiveTab}
      onCancel={() => navigate("/configurations/package")}
      onSave={handleSave}
      saving={saving}
      saveLabel={isEdit ? "Save Changes" : "Create Package"}
    >
      {activeTab === 0 && <GeneralTab state={state} update={update} />}
      {activeTab === 1 && <ServicesTab state={state} update={update} />}
      {activeTab === 2 && (
        <OperationalRequirementsTab
          state={state}
          update={update}
          roleOptions={roleOptions}
          rolesLoading={rolesLoading}
        />
      )}
      {activeTab === 3 && <PricingTab state={state} update={update} />}
    </PackageBuilderLayout>
  );
};

export default PackageBuilderPage;
